// src/modules/units/tyres/views/action/tyreactionform.view.js
//
// Pure markup for the Action Capture form. It renders whatever fields the schema
// declares for the given action — each field's `input` descriptor picks the
// control — and prefills record-backed fields from the current tyre so, e.g., an
// inspection opens showing the tyre's current tread as the starting point. No
// data fetching or writes here; the orchestrator (tyreactionform.js) wires it.

import { esc } from '../../shared/tyre-format.js';
import {
  normalizeActionType, getActionFields, actionVerb, actionLabel, actionTone
} from '../../shared/tyre-action-schema.js';
import { icon } from '../../shared/tyre-icons.js';
import { hasSensor } from '../../shared/tyre-status.js';

const isBlank = (v) => v === null || v === undefined || v === '';

function control(field, value) {
  const inp = field.input || { control: 'text' };
  const id = `tmaf-${esc(field.key)}`;
  const req = inp.required ? 'required' : '';

  if (inp.control === 'select') {
    const opts = ['<option value="">Select…</option>']
      .concat((inp.options || []).map((o) =>
        `<option value="${esc(o)}"${String(o) === String(value) ? ' selected' : ''}>${esc(o)}</option>`))
      .join('');
    return `<select id="${id}" class="tm-input" data-field="${esc(field.key)}" ${req}>${opts}</select>`;
  }

  // Radio cards, not a dropdown. A select hides its options until clicked, which
  // is the wrong trade on the one action that can't be undone — the reader should
  // see all three outcomes, and their consequences, before choosing.
  if (inp.control === 'choice') {
    const opts = (inp.options || []).map((o) => {
      const on = String(o.value) === String(value);
      return `
        <label class="tm-choice">
          <input type="radio" name="${id}" class="tm-choice-input" value="${esc(o.value)}"
                 data-field="${esc(field.key)}"${on ? ' checked' : ''} ${req} />
          <span class="tm-choice-body">
            <span class="tm-choice-label">${esc(o.label)}</span>
            ${o.hint ? `<span class="tm-choice-hint">${esc(o.hint)}</span>` : ''}
          </span>
        </label>`;
    }).join('');
    return `<div class="tm-choice-group" role="radiogroup" aria-label="${esc(field.label)}">${opts}</div>`;
  }

  if (inp.control === 'textarea') {
    return `<textarea id="${id}" class="tm-input tm-textarea" rows="2" data-field="${esc(field.key)}" ${req}>${esc(value)}</textarea>`;
  }

  if (inp.control === 'number') {
    const step = inp.step != null ? `step="${esc(inp.step)}"` : '';
    const min = inp.min != null ? `min="${esc(inp.min)}"` : '';
    const input = `<input id="${id}" type="number" inputmode="decimal" class="tm-input" data-field="${esc(field.key)}" ${step} ${min} value="${esc(value)}" ${req} />`;
    if (inp.prefix || inp.unit) {
      return `<div class="tm-input-affix">${inp.prefix ? `<span class="tm-affix tm-affix-pre">${esc(inp.prefix)}</span>` : ''}${input}${inp.unit ? `<span class="tm-affix tm-affix-post">${esc(inp.unit)}</span>` : ''}</div>`;
    }
    return input;
  }

  return `<input id="${id}" type="text" class="tm-input" data-field="${esc(field.key)}" value="${esc(value)}" ${req} />`;
}

function fieldRow(field, value) {
  const inp = field.input || {};
  const reqMark = inp.required ? '<span class="tm-req" aria-hidden="true">*</span>' : '';
  const choice = inp.control === 'choice';
  const wide = (inp.control === 'textarea' || choice) ? ' tm-form-row-wide' : '';
  // A radio group has no single input to label, so it gets a plain span and the
  // group carries its own aria-label instead of a dangling `for`.
  const label = choice
    ? `<span class="tm-form-label">${esc(field.label)}${reqMark}</span>`
    : `<label class="tm-form-label" for="tmaf-${esc(field.key)}">${esc(field.label)}${reqMark}</label>`;
  return `
    <div class="tm-form-row${wide}">
      ${label}
      ${control(field, value)}
    </div>`;
}

// A rotation is a normal outcome of an inspection: the tyre is moved to a new
// wheel position. It's shown only on the Inspect form and only for a fitted tyre
// (a stock tyre has no on-truck position to rotate). Logging it is independent of
// saving the inspection — it fires its own POST /action with just a date, since
// the sensor is unchanged and the system tracks the position and on-truck km.
function rotationInlineHTML(actionType, tyre) {
  if (normalizeActionType(actionType) !== 'inspect' || !hasSensor(tyre)) return '';
  return `
    <div class="tm-rotate-panel" data-rotate>
      <div class="tm-rotate-head">
        <span class="tm-rotate-ic tm-tone-amber">${icon('rotate')}</span>
        <div>
          <h3 class="tm-rotate-title">Rotate to a new position?</h3>
          <p class="tm-rotate-sub">This will log a seperate action and is separate from saving the inspection.</p>
        </div>
      </div>
      <div class="tm-form-msg" data-rotate-msg hidden></div>
      <div class="tm-rotate-body">
        <div class="tm-rotate-field">
          <label class="tm-form-label" for="tmaf-rotate-date">Rotation date</label>
          <input type="date" id="tmaf-rotate-date" class="tm-input" data-rotate-date />
        </div>
        <div class="tm-rotate-actions">
          <button type="button" class="tm-btn-secondary" data-rotate-save>Log rotation</button>
        </div>
      </div>
    </div>`;
}

export function actionFormHTML(actionType, tyre = {}) {
  const t = normalizeActionType(actionType);
  const fields = getActionFields(t);
  const rows = fields.map((f) => {
    const seeded = f.record && !isBlank(tyre[f.record]) ? tyre[f.record] : '';
    return fieldRow(f, seeded);
  }).join('');

  return `
    <div class="tm-form">
      <div class="tm-form-head">
        <div class="tm-form-heading">
          <h2 class="tm-form-title">${esc(actionVerb(t))}</h2>
          <p class="tm-form-sub">Tyre <b>${esc(tyre.externalTyreId || '—')}</b></p>
        </div>
        <span class="tm-badge tm-tone-${actionTone(t)}">${esc(actionLabel(t))}</span>
      </div>
      <div class="tm-form-msg" data-msg hidden></div>
      <div class="tm-form-grid">${rows}</div>

      <div class="tm-form-actions">
        <button type="button" class="tm-btn-secondary" data-cancel>Cancel</button>
        <button type="button" class="tm-btn-primary" data-submit>Save ${esc(actionVerb(t).toLowerCase())}</button>
      </div>
    </div>`;
}
